import { action, makeAutoObservable, runInAction } from 'mobx';
import { State } from '~@types/store';
import { FieldValues } from '~modules/devices/components/DeviceConfigForm/schema';
import { UserAction } from '~config/toastNotification';
import { t } from '~i18n';
import { failureMessage } from '~utils/error';
import { Device, DeviceType, WatcherrApiNotFoundError } from '@ixicare/resources';

// TODO: type via @ixicare/resources once the resource is integrated.
export type OtaJob = Record<string, unknown>;

/**
 * What the device runs after a write, without re-reading it: the service recollates off the back of
 * the job the write queued and has not done so yet, so a read would still return the old values.
 *
 * A cleared key is dropped rather than overwritten. What the device falls back to is the site's
 * value or the device type default, and this store knows neither -- but leaving the old value in
 * place is worse than not knowing, because a value no layer accounts for is exactly what the form
 * reports as a setting the device has not been updated to.
 */
const collatedAfterWrite = (collated: FieldValues, saved: FieldValues, cleared: string[]): FieldValues => {
  const next = { ...collated, ...saved };
  for (const key of cleared) delete next[key];
  return next;
};

class DeviceStore {
  rootStore: State;
  uuid: string | null = null;
  id: number | null = null;
  imeiNumber: string | null = null;
  deviceType: DeviceType | null = null;
  macAddress: string | null = null;
  phoneNumber: string | null = null;
  siteId: number | null = null;
  // The firmware the device reports. Shapes the config form: defaults and which fields exist both
  // depend on it.
  swVersion: string | null = null;
  // The configuration the device runs: defaults, site and its own level collated by the service.
  configValues: FieldValues = {};
  // The device's own level on its own -- only what it diverges by, which is what tells an override
  // apart from a value it inherits.
  deviceOverrides: FieldValues = {};
  otaJob: OtaJob | null = null;
  loadingSettings: boolean = false;
  loading: boolean = false;
  saving: boolean = false;
  error: Error | null = null;
  status: string;

  constructor(rootStore: State) {
    makeAutoObservable(this, {
      rootStore: false,
      loadDevice: action.bound,
      loadDeviceSettings: action.bound,
      saveConfig: action.bound,
      resetDeviceOverrides: action.bound,
      getConfigStatus: action.bound,
      hydrate: action.bound,
    });
    this.rootStore = rootStore;
  }

  hydrate(meta: { macAddress: string; deviceType: DeviceType }) {
    this.macAddress = meta.macAddress;
    this.deviceType = meta.deviceType;
  }

  async loadDevice(uuid: string) {
    if (this.loading && this.uuid === uuid) return Promise.resolve();
    this.loading = true;
    this.uuid = uuid;

    return Device.getDevice(uuid).then((res) => {
      runInAction(() => {
        // Drop a response that resolved after we navigated to another device.
        if (this.uuid !== uuid) return;
        this.loading = false;
        this.id = res.id;
        this.macAddress = res.macAddress;
        this.phoneNumber = res.phoneNumber ?? null;
        this.imeiNumber = res.imeiNumber ?? null;
        this.deviceType = res?.deviceType || null;
        this.swVersion = res.swVersion ?? null;
        this.siteId = res.siteId ?? null;
      });
    });
  }

  async loadDeviceSettings(uuid: string) {
    if (this.loadingSettings && this.uuid === uuid) return Promise.resolve();
    this.loadingSettings = true;
    this.uuid = uuid;
    this.configValues = {};
    this.deviceOverrides = {};

    // Two reads: the collated configuration is what the device runs and what the form is seeded
    // with, its own level is what says where each of those values came from. The level only drives
    // an annotation, so failing to read it must not keep the form off the screen.
    return Promise.all([
      Device.getCurrentDeviceSettings(uuid),
      Device.getDeviceSettings(uuid).catch(() => ({}) as FieldValues),
    ]).then(([current, overrides]) => {
      runInAction(() => {
        if (this.uuid !== uuid) return;
        this.loadingSettings = false;
        this.configValues = current.currentConfiguration ?? {};
        this.deviceOverrides = overrides ?? {};
      });
    });
  }

  async getConfigStatus() {
    if (!this.uuid) return;
    try {
      const job = await Device.getDeviceSettingsOtaJobStatus(this.uuid);
      runInAction(() => {
        this.otaJob = job as unknown as OtaJob;
      });
    } catch (e) {
      if (e instanceof WatcherrApiNotFoundError) {
        runInAction(() => {
          this.otaJob = null;
        });
        return;
      }
      throw e;
    }
  }

  async saveConfig(payload: FieldValues): Promise<FieldValues> {
    if (!this.uuid) throw new Error('No device loaded');
    this.saving = true;
    this.error = null;
    // `phone_number` is a Device model attribute, not a config setting — route
    // it to Device.patchDevice. The rest goes to the settings endpoint.
    const { phone_number, ...config } = payload;
    const patchPhone = phone_number !== undefined;
    try {
      // Null until something is written, so a save of `phone_number` alone is not mistaken for a
      // write that emptied the device's configuration level.
      let saved: FieldValues | null = null;
      if (Object.keys(config).length > 0) {
        saved = (await Device.postDeviceSettings(this.uuid, config)).deviceLevelConfiguration;
      }
      let newPhone = this.phoneNumber;
      if (patchPhone && this.id != null) {
        newPhone = (await Device.patchDevice(this.id, { phoneNumber: String(phone_number ?? '') })).phoneNumber ?? null;
      }
      runInAction(() => {
        if (saved) {
          // The response is the device's own level after the write, so it replaces the overrides
          // outright. The keys sent as null are gone from it, so they are named separately: the
          // response cannot say what it no longer contains.
          this.deviceOverrides = saved;
          this.configValues = collatedAfterWrite(
            this.configValues,
            saved,
            Object.keys(config).filter((key) => config[key] === null)
          );
        }
        this.phoneNumber = newPhone;
        this.saving = false;
        this.rootStore.notifications.addUserActionNotification({
          type: UserAction.DeviceConfigUpdate,
          notificationType: 'success',
          message: t('alerts.device.config.update.success'),
        });
      });
      return saved ?? {};
    } catch (e) {
      runInAction(() => {
        this.error = e as Error;
        this.saving = false;
        this.rootStore.notifications.addUserActionNotification({
          type: UserAction.DeviceConfigUpdate,
          notificationType: 'error',
          message: failureMessage(e, t('alerts.device.config.update.failure')),
        });
      });
      throw e;
    }
  }

  /**
   * Drop the device's own configuration level, leaving it on the site's values and the type
   * defaults beneath them.
   *
   * The service clears a parameter when it is sent as null, so emptying the level means naming
   * every key it currently holds. Only the keys the device actually overrides are sent: a null for
   * a key it never set would be a write with nothing to undo.
   */
  async resetDeviceOverrides(): Promise<void> {
    if (!this.uuid) throw new Error('No device loaded');
    const keys = Object.keys(this.deviceOverrides);
    if (keys.length === 0) return;

    this.saving = true;
    this.error = null;
    try {
      await Device.postDeviceSettings(this.uuid, Object.fromEntries(keys.map((key) => [key, null])));
      runInAction(() => {
        // The level is empty by construction now, so it is set rather than read back from the
        // response, which is null or {} depending on how the service renders an emptied level.
        this.deviceOverrides = {};
        this.configValues = collatedAfterWrite(this.configValues, {}, keys);
        this.saving = false;
        this.rootStore.notifications.addUserActionNotification({
          type: UserAction.DeviceConfigUpdate,
          notificationType: 'success',
          message: t('alerts.device-config.reset.success'),
        });
      });
    } catch (e) {
      runInAction(() => {
        this.error = e as Error;
        this.saving = false;
        this.rootStore.notifications.addUserActionNotification({
          type: UserAction.DeviceConfigUpdate,
          notificationType: 'error',
          message: failureMessage(e, t('alerts.device-config.reset.failure')),
        });
      });
      throw e;
    }
  }

  reset() {
    this.uuid = null;
    this.id = null;
    this.deviceType = null;
    this.macAddress = null;
    this.phoneNumber = null;
    this.imeiNumber = null;
    this.siteId = null;
    this.swVersion = null;
    this.configValues = {};
    this.deviceOverrides = {};
    this.otaJob = null;
    this.loadingSettings = false;
    this.loading = false;
    this.saving = false;
    this.error = null;
  }
}

export default DeviceStore;
